import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Download, FileText, DollarSign, TrendingUp, TrendingDown, BarChart3, Mail, Building2, Calendar, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import html2pdf from 'html2pdf.js';

const REPORT_TYPES = {
  incomeStatement: {
    id: 'income-statement',
    name: 'Income Statement (Statement of Comprehensive Income)',
    description: 'Summarizes revenues, expenses, and profit/loss over a period',
    category: 'Core Statements'
  },
  balanceSheet: {
    id: 'balance-sheet',
    name: 'Balance Sheet (Statement of Financial Position)',
    description: 'Shows assets, liabilities, and equity at a specific date',
    category: 'Core Statements'
  },
  cashFlowStatement: {
    id: 'cash-flow',
    name: 'Cash Flow Statement',
    description: 'Shows cash inflows and outflows from operating, investing, and financing activities',
    category: 'Core Statements'
  },
  changesInEquity: {
    id: 'changes-equity',
    name: 'Statement of Changes in Equity',
    description: 'Details movements in equity over a reporting period',
    category: 'Core Statements'
  },
  trialBalance: {
    id: 'trial-balance',
    name: 'Trial Balance',
    description: 'Lists all general ledger accounts with debit/credit balances',
    category: 'Accounting Reports'
  },
  generalLedger: {
    id: 'general-ledger',
    name: 'General Ledger (GL)',
    description: 'Detailed record of all financial transactions grouped by account',
    category: 'Accounting Reports'
  },
  accountsReceivable: {
    id: 'accounts-receivable',
    name: 'Accounts Receivable (Debtors) Summary',
    description: 'Shows amounts owed by customers',
    category: 'Accounting Reports'
  },
  accountsPayable: {
    id: 'accounts-payable',
    name: 'Accounts Payable (Creditors) Summary',
    description: 'Shows amounts owed to suppliers',
    category: 'Accounting Reports'
  },
  fixedAssetRegister: {
    id: 'fixed-asset',
    name: 'Fixed Asset Register',
    description: 'Tracks assets, purchase costs, depreciation, and net book value',
    category: 'Accounting Reports'
  },
  budgetVsActual: {
    id: 'budget-actual',
    name: 'Budget vs. Actual Report',
    description: 'Compares planned figures to actual performance',
    category: 'Management Reports'
  },
  departmentPerformance: {
    id: 'department-performance',
    name: 'Statement of Financial Performance by Department/Segment',
    description: 'Breaks down income and expenses by business unit or project',
    category: 'Management Reports'
  },
  notesToFinancials: {
    id: 'notes-financials',
    name: 'Notes to the Financial Statements',
    description: 'Detailed explanations, accounting policies, and supporting data',
    category: 'Management Reports'
  }
};

// Professional Report Styles
const reportStyles = {
  page: {
    fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    backgroundColor: '#ffffff',
    color: '#1e293b',
    lineHeight: 1.6
  },
  header: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
    color: '#ffffff',
    padding: '40px',
    marginBottom: '0',
    position: 'relative',
    overflow: 'hidden'
  },
  headerPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    opacity: 0.5
  },
  headerContent: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  logo: {
    width: '70px',
    height: '70px',
    borderRadius: '12px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    backdropFilter: 'blur(10px)'
  },
  companyInfo: {
    textAlign: 'left'
  },
  companyName: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '4px',
    letterSpacing: '-0.5px'
  },
  companyDetails: {
    fontSize: '13px',
    opacity: 0.85,
    lineHeight: 1.5
  },
  reportTitle: {
    textAlign: 'right'
  },
  reportTitleText: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '8px',
    letterSpacing: '-0.5px'
  },
  reportSubtitle: {
    fontSize: '14px',
    opacity: 0.85,
    marginBottom: '12px'
  },
  periodBadge: {
    display: 'inline-block',
    backgroundColor: 'rgba(255,255,255,0.15)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
    backdropFilter: 'blur(10px)'
  },
  metaBar: {
    backgroundColor: '#f8fafc',
    padding: '16px 40px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: '#64748b'
  },
  section: {
    padding: '32px 40px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  sectionIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: '#eff6ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#3b82f6'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
    marginBottom: '32px'
  },
  summaryCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
    position: 'relative',
    overflow: 'hidden'
  },
  summaryCardAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px'
  },
  summaryCardLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px'
  },
  summaryCardValue: {
    fontSize: '28px',
    fontWeight: '700',
    letterSpacing: '-0.5px'
  },
  summaryCardIcon: {
    position: 'absolute',
    right: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    opacity: 0.1,
    fontSize: '64px'
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '14px'
  },
  tableHeader: {
    backgroundColor: '#f8fafc',
    textAlign: 'left',
    padding: '14px 16px',
    fontWeight: '600',
    color: '#475569',
    borderBottom: '2px solid #e2e8f0',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tableCell: {
    padding: '14px 16px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155'
  },
  tableCellRight: {
    padding: '14px 16px',
    borderBottom: '1px solid #f1f5f9',
    textAlign: 'right',
    fontWeight: '600',
    color: '#0f172a'
  },
  tableTotal: {
    backgroundColor: '#f1f5f9',
    fontWeight: '700',
    color: '#0f172a'
  },
  footer: {
    backgroundColor: '#f8fafc',
    padding: '24px 40px',
    borderTop: '1px solid #e2e8f0',
    marginTop: '32px',
    fontSize: '12px',
    color: '#64748b',
    textAlign: 'center'
  },
  netIncomeBanner: {
    borderRadius: '16px',
    padding: '28px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px'
  },
  watermark: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    fontSize: '10px',
    color: '#cbd5e1',
    fontStyle: 'italic'
  }
};

const Reports = () => {
  const { company } = useAuth();
  const { formatCurrency, currencySymbol } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState('incomeStatement');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    asOfDate: new Date().toISOString().split('T')[0]
  });

  const handleDateChange = (e) => {
    setDateRange({
      ...dateRange,
      [e.target.name]: e.target.value
    });
  };

  const handleReportTypeChange = (e) => {
    setSelectedReportType(e.target.value);
    setReportData(null);
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const reportConfig = REPORT_TYPES[selectedReportType];
      const reportType = reportConfig?.id || selectedReportType;
      
      const params = reportType === 'balance-sheet' 
        ? { asOfDate: dateRange.asOfDate }
        : { startDate: dateRange.startDate, endDate: dateRange.endDate };
      
      const response = await axios.get(`/api/dashboard/reports/${reportType}`, { params });
      setReportData({
        ...response.data,
        reportType: selectedReportType,
        reportName: reportConfig?.name || 'Report'
      });
    } catch (error) {
      console.error('Report generation error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Failed to generate report';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReportEmail = async () => {
    if (!reportData) {
      toast.error('Please generate a report first');
      return;
    }

    const email = prompt('Enter email address(es) to send the report to (separate multiple emails with commas):');
    if (!email || !email.trim()) return;

    try {
      const reportConfig = REPORT_TYPES[selectedReportType];
      const reportType = reportConfig?.id || selectedReportType;
      
      const params = reportType === 'balance-sheet' 
        ? { asOfDate: dateRange.asOfDate }
        : { startDate: dateRange.startDate, endDate: dateRange.endDate };

      await axios.post('/api/dashboard/reports/send-email', {
        reportType,
        reportName: reportConfig?.name || 'Financial Report',
        emails: email.split(',').map(e => e.trim()).filter(e => e),
        dateRange,
        params
      });
      toast.success('Financial report sent via email successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send report email');
    }
  };

  const selectedReport = REPORT_TYPES[selectedReportType] || REPORT_TYPES.incomeStatement;

  const reportsByCategory = Object.values(REPORT_TYPES).reduce((acc, report) => {
    if (!acc[report.category]) acc[report.category] = [];
    acc[report.category].push(report);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Financial Reports</h1>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Report Configuration</h3>
        </div>

        <div className="form-group">
          <label className="form-label">Report Type *</label>
          <select
            className="form-control"
            value={selectedReportType}
            onChange={handleReportTypeChange}
            style={{ fontSize: '16px', padding: '12px' }}
          >
            {Object.entries(reportsByCategory).map(([category, reports]) => (
              <optgroup key={category} label={category}>
                {reports.map(report => {
                  const reportKey = Object.keys(REPORT_TYPES).find(key => REPORT_TYPES[key].id === report.id);
                  return (
                    <option key={report.id} value={reportKey || report.id}>
                      {report.name}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
          {selectedReport.description && (
            <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
              {selectedReport.description}
            </p>
          )}
        </div>

        <div className="grid grid-3">
          {REPORT_TYPES[selectedReportType]?.id === 'balance-sheet' ? (
            <>
              <div className="form-group">
                <label className="form-label">As Of Date *</label>
                <input type="date" name="asOfDate" className="form-control" value={dateRange.asOfDate} onChange={handleDateChange} />
              </div>
              <div className="form-group"></div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Start Date *</label>
                <input type="date" name="startDate" className="form-control" value={dateRange.startDate} onChange={handleDateChange} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date *</label>
                <input type="date" name="endDate" className="form-control" value={dateRange.endDate} onChange={handleDateChange} />
              </div>
            </>
          )}
          <div className="form-group">
            <label className="form-label">&nbsp;</label>
            <button className="btn btn-primary" onClick={generateReport} disabled={loading} style={{ width: '100%' }}>
              <BarChart3 size={18} />
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {reportData && (
        <ReportDisplayWrapper
          reportData={reportData}
          dateRange={dateRange}
          selectedReportType={selectedReportType}
          company={company}
          onSendEmail={handleSendReportEmail}
        />
      )}
    </div>
  );
};

// Report Display Wrapper with PDF download
const ReportDisplayWrapper = ({ reportData, dateRange, selectedReportType, company, onSendEmail }) => {
  const pdfRef = useRef(null);

  const handleDownloadPDF = async () => {
    if (!pdfRef.current) {
      toast.error('Report not ready for PDF generation');
      return;
    }

    try {
      const element = pdfRef.current;
      const reportConfig = REPORT_TYPES[selectedReportType];
      const reportName = reportConfig?.name || 'Report';
      const opt = {
        margin: [0.3, 0.3, 0.3, 0.3],
        filename: `${reportName.replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      const buttons = element.querySelectorAll('.no-print');
      buttons.forEach(btn => btn.style.display = 'none');

      await html2pdf().set(opt).from(element).save();

      buttons.forEach(btn => btn.style.display = '');
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <>
      <div style={{ marginBottom: '20px', marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }} className="no-print">
        <button className="btn btn-outline" onClick={onSendEmail} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Mail size={18} /> Email Report
        </button>
        <button className="btn btn-primary" onClick={handleDownloadPDF} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Download size={18} /> Download PDF
        </button>
      </div>
      <div ref={pdfRef} style={reportStyles.page}>
        <ReportDisplay 
          reportData={reportData} 
          dateRange={dateRange}
          selectedReportType={selectedReportType}
          company={company}
        />
      </div>
    </>
  );
};

const ReportDisplay = ({ reportData, dateRange, selectedReportType, company }) => {
  const { formatCurrency } = useCurrency();
  const reportTypeId = reportData?.reportType || selectedReportType;
  const reportConfig = REPORT_TYPES[reportTypeId];
  const actualReportId = reportConfig?.id || reportTypeId;
  
  switch (actualReportId) {
    case 'income-statement':
      return <IncomeStatementReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'balance-sheet':
      return <BalanceSheetReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'cash-flow':
      return <CashFlowReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'accounts-receivable':
      return <AccountsReceivableReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'accounts-payable':
      return <AccountsPayableReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'trial-balance':
      return <TrialBalanceReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'fixed-asset':
      return <FixedAssetReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'budget-actual':
      return <BudgetVsActualReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'department-performance':
      return <DepartmentPerformanceReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    case 'notes-financials':
      return <NotesToFinancialsReport data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
    default:
      return <GenericReportDisplay data={reportData} dateRange={dateRange} company={company} formatCurrency={formatCurrency} />;
  }
};

// Professional Report Header
const ProfessionalHeader = ({ title, subtitle, dateRange, company, asOfDate }) => (
  <>
    <div style={reportStyles.header}>
      <div style={reportStyles.headerPattern}></div>
      <div style={reportStyles.headerContent}>
        <div style={reportStyles.logoSection}>
          <div style={reportStyles.logo}>
            {company?.logoUrl || company?.logo_url ? (
              <img src={company.logoUrl || company.logo_url} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <Building2 size={36} color="#ffffff" />
            )}
          </div>
          <div style={reportStyles.companyInfo}>
            <div style={reportStyles.companyName}>{company?.name || 'Company Name'}</div>
            <div style={reportStyles.companyDetails}>
              {company?.address && <div>{company.address}</div>}
              {(company?.city || company?.country) && <div>{[company?.city, company?.country].filter(Boolean).join(', ')}</div>}
              {company?.email && <div>{company.email}</div>}
            </div>
          </div>
        </div>
        <div style={reportStyles.reportTitle}>
          <div style={reportStyles.reportTitleText}>{title}</div>
          {subtitle && <div style={reportStyles.reportSubtitle}>{subtitle}</div>}
          <div style={reportStyles.periodBadge}>
            <Calendar size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
            {asOfDate ? (
              `As of ${new Date(asOfDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
            ) : (
              `${new Date(dateRange.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ??? ${new Date(dateRange.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            )}
          </div>
        </div>
      </div>
    </div>
    <div style={reportStyles.metaBar}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Clock size={14} />
        Generated on {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {new Date().toLocaleTimeString()}
      </div>
      <div style={{ fontWeight: '600', color: '#0f172a' }}>
        CONFIDENTIAL
      </div>
    </div>
  </>
);

// Summary Card Component
const SummaryCard = ({ label, value, color, icon: Icon, formatCurrency }) => {
  const colorSchemes = {
    success: { accent: '#10b981', bg: '#ecfdf5', text: '#065f46' },
    danger: { accent: '#ef4444', bg: '#fef2f2', text: '#991b1b' },
    info: { accent: '#3b82f6', bg: '#eff6ff', text: '#1e40af' },
    warning: { accent: '#f59e0b', bg: '#fffbeb', text: '#92400e' },
    purple: { accent: '#8b5cf6', bg: '#f5f3ff', text: '#5b21b6' }
  };
  const scheme = colorSchemes[color] || colorSchemes.info;

  return (
    <div style={{ ...reportStyles.summaryCard, backgroundColor: scheme.bg }}>
      <div style={{ ...reportStyles.summaryCardAccent, backgroundColor: scheme.accent }}></div>
      <div style={{ ...reportStyles.summaryCardLabel, color: scheme.text }}>{label}</div>
      <div style={{ ...reportStyles.summaryCardValue, color: scheme.accent }}>
        {formatCurrency(value || 0)}
      </div>
      {Icon && (
        <div style={{ ...reportStyles.summaryCardIcon, color: scheme.accent }}>
          <Icon size={64} />
        </div>
      )}
    </div>
  );
};

// Income Statement Report
const IncomeStatementReport = ({ data, dateRange, company, formatCurrency }) => {
  const netIncomePositive = (data.netIncome || 0) >= 0;
  
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader 
        title="Income Statement"
        subtitle="Statement of Comprehensive Income"
        dateRange={dateRange}
        company={company}
      />
      
      <div style={reportStyles.section}>
        <div style={reportStyles.summaryGrid}>
          <SummaryCard label="Total Revenue" value={data.totalIncome} color="success" icon={TrendingUp} formatCurrency={formatCurrency} />
          <SummaryCard label="Total Expenses" value={data.totalExpenses} color="danger" icon={TrendingDown} formatCurrency={formatCurrency} />
          <SummaryCard label="Net Income" value={data.netIncome} color={netIncomePositive ? 'success' : 'danger'} icon={DollarSign} formatCurrency={formatCurrency} />
        </div>

        <div style={reportStyles.sectionTitle}>
          <div style={reportStyles.sectionIcon}><FileText size={18} /></div>
          Financial Summary
        </div>

        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Description</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={reportStyles.tableCell}>
                <div style={{ fontWeight: '600' }}>Total Revenue</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Income from all sources</div>
              </td>
              <td style={{ ...reportStyles.tableCellRight, color: '#10b981' }}>
                {formatCurrency(data.totalIncome || 0)}
              </td>
            </tr>
            <tr>
              <td style={reportStyles.tableCell}>
                <div style={{ fontWeight: '600' }}>Total Operating Expenses</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Cost of operations</div>
              </td>
              <td style={{ ...reportStyles.tableCellRight, color: '#ef4444' }}>
                ({formatCurrency(data.totalExpenses || 0)})
              </td>
            </tr>
            <tr style={reportStyles.tableTotal}>
              <td style={{ ...reportStyles.tableCell, fontWeight: '700', fontSize: '16px' }}>
                Net Income / (Loss)
              </td>
              <td style={{ 
                ...reportStyles.tableCellRight, 
                fontSize: '18px',
                color: netIncomePositive ? '#10b981' : '#ef4444'
              }}>
                {formatCurrency(data.netIncome || 0)}
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{
          ...reportStyles.netIncomeBanner,
          backgroundColor: netIncomePositive ? '#ecfdf5' : '#fef2f2',
          border: `2px solid ${netIncomePositive ? '#10b981' : '#ef4444'}`
        }}>
          <div>
            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Net Result for Period</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {netIncomePositive ? 'The company generated a profit during this period.' : 'The company incurred a loss during this period.'}
            </div>
          </div>
          <div style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            color: netIncomePositive ? '#10b981' : '#ef4444'
          }}>
            {formatCurrency(data.netIncome || 0)}
          </div>
        </div>
      </div>

      {data.expensesByCategory && data.expensesByCategory.length > 0 && (
        <div style={{ ...reportStyles.section, borderTop: '1px solid #e2e8f0' }}>
          <div style={reportStyles.sectionTitle}>
            <div style={reportStyles.sectionIcon}><BarChart3 size={18} /></div>
            Expenses Breakdown by Category
          </div>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.expensesByCategory} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tickFormatter={(val) => formatCurrency(val)} tick={{ fontSize: 12 }} />
                <YAxis dataKey="category" type="category" width={150} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Bar dataKey="total" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div style={reportStyles.footer}>
        <div style={{ marginBottom: '8px', fontWeight: '600', color: '#0f172a' }}>{company?.name || 'Company Name'}</div>
        <div>This report was generated automatically by DynaFinances. For questions, contact your financial administrator.</div>
        <div style={{ marginTop: '8px' }}>?? {new Date().getFullYear()} All Rights Reserved</div>
      </div>
    </div>
  );
};

// Balance Sheet Report
const BalanceSheetReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data) return <div style={{ padding: '40px', textAlign: 'center' }}>No data available</div>;
  
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader 
        title="Balance Sheet"
        subtitle="Statement of Financial Position"
        dateRange={dateRange}
        company={company}
        asOfDate={dateRange.asOfDate}
      />
      
      <div style={reportStyles.section}>
        <div style={reportStyles.summaryGrid}>
          <SummaryCard label="Total Assets" value={data.totalAssets} color="info" icon={TrendingUp} formatCurrency={formatCurrency} />
          <SummaryCard label="Total Liabilities" value={data.totalLiabilities} color="danger" icon={TrendingDown} formatCurrency={formatCurrency} />
          <SummaryCard label="Total Equity" value={data.totalEquity} color="success" icon={DollarSign} formatCurrency={formatCurrency} />
        </div>

        {/* Assets */}
        <div style={reportStyles.sectionTitle}>
          <div style={{ ...reportStyles.sectionIcon, backgroundColor: '#eff6ff' }}><TrendingUp size={18} color="#3b82f6" /></div>
          Assets
        </div>
        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Account</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.assets && data.assets.length > 0 ? (
              <>
                {data.assets.map((item, idx) => (
                  <tr key={idx}>
                    <td style={reportStyles.tableCell}>{item.account}</td>
                    <td style={reportStyles.tableCellRight}>{formatCurrency(item.amount || 0)}</td>
                  </tr>
                ))}
                <tr style={reportStyles.tableTotal}>
                  <td style={{ ...reportStyles.tableCell, fontWeight: '700' }}>Total Assets</td>
                  <td style={{ ...reportStyles.tableCellRight, color: '#3b82f6' }}>{formatCurrency(data.totalAssets || 0)}</td>
                </tr>
              </>
            ) : (
              <tr><td colSpan="2" style={{ ...reportStyles.tableCell, textAlign: 'center', color: '#64748b' }}>No asset data available</td></tr>
            )}
          </tbody>
        </table>

        {/* Liabilities */}
        <div style={{ ...reportStyles.sectionTitle, marginTop: '32px' }}>
          <div style={{ ...reportStyles.sectionIcon, backgroundColor: '#fef2f2' }}><TrendingDown size={18} color="#ef4444" /></div>
          Liabilities
        </div>
        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Account</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.liabilities && data.liabilities.length > 0 ? (
              <>
                {data.liabilities.map((item, idx) => (
                  <tr key={idx}>
                    <td style={reportStyles.tableCell}>{item.account}</td>
                    <td style={reportStyles.tableCellRight}>{formatCurrency(item.amount || 0)}</td>
                  </tr>
                ))}
                <tr style={reportStyles.tableTotal}>
                  <td style={{ ...reportStyles.tableCell, fontWeight: '700' }}>Total Liabilities</td>
                  <td style={{ ...reportStyles.tableCellRight, color: '#ef4444' }}>{formatCurrency(data.totalLiabilities || 0)}</td>
                </tr>
              </>
            ) : (
              <tr><td colSpan="2" style={{ ...reportStyles.tableCell, textAlign: 'center', color: '#64748b' }}>No liability data available</td></tr>
            )}
          </tbody>
        </table>

        {/* Equity */}
        <div style={{ ...reportStyles.sectionTitle, marginTop: '32px' }}>
          <div style={{ ...reportStyles.sectionIcon, backgroundColor: '#ecfdf5' }}><DollarSign size={18} color="#10b981" /></div>
          Equity
        </div>
        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Account</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.equity && data.equity.length > 0 ? (
              <>
                {data.equity.map((item, idx) => (
                  <tr key={idx}>
                    <td style={reportStyles.tableCell}>{item.account}</td>
                    <td style={reportStyles.tableCellRight}>{formatCurrency(item.amount || 0)}</td>
                  </tr>
                ))}
                <tr style={reportStyles.tableTotal}>
                  <td style={{ ...reportStyles.tableCell, fontWeight: '700' }}>Total Equity</td>
                  <td style={{ ...reportStyles.tableCellRight, color: '#10b981' }}>{formatCurrency(data.totalEquity || 0)}</td>
                </tr>
              </>
            ) : (
              <tr><td colSpan="2" style={{ ...reportStyles.tableCell, textAlign: 'center', color: '#64748b' }}>No equity data available</td></tr>
            )}
          </tbody>
        </table>

        {/* Balance Check */}
        <div style={{
          ...reportStyles.netIncomeBanner,
          backgroundColor: '#f8fafc',
          border: '2px solid #3b82f6',
          marginTop: '32px'
        }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Accounting Equation</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>Assets = Liabilities + Equity</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>{formatCurrency(data.totalAssets || 0)}</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>= {formatCurrency((data.totalLiabilities || 0) + (data.totalEquity || 0))}</div>
          </div>
        </div>
      </div>

      <div style={reportStyles.footer}>
        <div style={{ marginBottom: '8px', fontWeight: '600', color: '#0f172a' }}>{company?.name || 'Company Name'}</div>
        <div>This report was generated automatically by DynaFinances.</div>
        <div style={{ marginTop: '8px' }}>?? {new Date().getFullYear()} All Rights Reserved</div>
      </div>
    </div>
  );
};

// Cash Flow Report
const CashFlowReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data) return <div style={{ padding: '40px', textAlign: 'center' }}>No data available</div>;
  
  const renderSection = (title, items, total, totalLabel, color) => (
    <>
      <div style={{ ...reportStyles.sectionTitle, marginTop: '24px' }}>
        <div style={{ ...reportStyles.sectionIcon, backgroundColor: color === 'success' ? '#ecfdf5' : color === 'info' ? '#eff6ff' : '#fef3c7' }}>
          <DollarSign size={18} color={color === 'success' ? '#10b981' : color === 'info' ? '#3b82f6' : '#f59e0b'} />
        </div>
        {title}
      </div>
      <table style={reportStyles.table}>
        <thead>
          <tr>
            <th style={reportStyles.tableHeader}>Item</th>
            <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items && items.length > 0 ? (
            <>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td style={reportStyles.tableCell}>{item.description}</td>
                  <td style={reportStyles.tableCellRight}>{formatCurrency(item.amount || 0)}</td>
                </tr>
              ))}
              <tr style={reportStyles.tableTotal}>
                <td style={{ ...reportStyles.tableCell, fontWeight: '700' }}>{totalLabel}</td>
                <td style={reportStyles.tableCellRight}>{formatCurrency(total || 0)}</td>
              </tr>
            </>
          ) : (
            <tr><td colSpan="2" style={{ ...reportStyles.tableCell, textAlign: 'center', color: '#64748b' }}>No data available</td></tr>
          )}
        </tbody>
      </table>
    </>
  );

  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader title="Cash Flow Statement" dateRange={dateRange} company={company} />
      
      <div style={reportStyles.section}>
        {renderSection('Operating Activities', data.operatingActivities, data.netOperatingCash, 'Net Cash from Operating Activities', 'success')}
        {renderSection('Investing Activities', data.investingActivities, data.netInvestingCash, 'Net Cash from Investing Activities', 'info')}
        {renderSection('Financing Activities', data.financingActivities, data.netFinancingCash, 'Net Cash from Financing Activities', 'warning')}

        <div style={{
          ...reportStyles.netIncomeBanner,
          backgroundColor: '#ecfdf5',
          border: '2px solid #10b981',
          marginTop: '32px'
        }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Net Change in Cash</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>Total cash movement for the period</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#10b981' }}>
            {formatCurrency(data.netChangeInCash || 0)}
          </div>
        </div>
      </div>

      <div style={reportStyles.footer}>
        <div style={{ marginBottom: '8px', fontWeight: '600', color: '#0f172a' }}>{company?.name || 'Company Name'}</div>
        <div>?? {new Date().getFullYear()} All Rights Reserved</div>
      </div>
    </div>
  );
};

// Accounts Receivable Report
const AccountsReceivableReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data) return <div style={{ padding: '40px', textAlign: 'center' }}>No data available</div>;
  
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader title="Accounts Receivable" subtitle="Debtors Summary" dateRange={dateRange} company={company} />
      
      <div style={reportStyles.section}>
        <div style={{ ...reportStyles.summaryGrid, gridTemplateColumns: '1fr' }}>
          <SummaryCard label="Total Accounts Receivable" value={data.totalReceivable} color="info" icon={DollarSign} formatCurrency={formatCurrency} />
        </div>

        <div style={reportStyles.sectionTitle}>
          <div style={reportStyles.sectionIcon}><FileText size={18} /></div>
          Outstanding Invoices
        </div>

        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Customer</th>
              <th style={reportStyles.tableHeader}>Invoice #</th>
              <th style={reportStyles.tableHeader}>Date</th>
              <th style={reportStyles.tableHeader}>Due Date</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Amount Due</th>
              <th style={reportStyles.tableHeader}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.invoices && data.invoices.length > 0 ? (
              data.invoices.map((invoice, idx) => (
                <tr key={idx}>
                  <td style={reportStyles.tableCell}>{invoice.customer_name}</td>
                  <td style={reportStyles.tableCell}>{invoice.invoice_number}</td>
                  <td style={reportStyles.tableCell}>{new Date(invoice.invoice_date).toLocaleDateString()}</td>
                  <td style={reportStyles.tableCell}>{new Date(invoice.due_date).toLocaleDateString()}</td>
                  <td style={reportStyles.tableCellRight}>{formatCurrency(invoice.amount_due || 0)}</td>
                  <td style={reportStyles.tableCell}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: invoice.status === 'overdue' ? '#fef2f2' : '#fef3c7',
                      color: invoice.status === 'overdue' ? '#dc2626' : '#d97706'
                    }}>
                      {invoice.status}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" style={{ ...reportStyles.tableCell, textAlign: 'center', color: '#64748b' }}>No outstanding invoices</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={reportStyles.footer}>
        <div>?? {new Date().getFullYear()} {company?.name || 'Company Name'}</div>
      </div>
    </div>
  );
};

// Accounts Payable Report
const AccountsPayableReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data) return <div style={{ padding: '40px', textAlign: 'center' }}>No data available</div>;
  
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader title="Accounts Payable" subtitle="Creditors Summary" dateRange={dateRange} company={company} />
      
      <div style={reportStyles.section}>
        <div style={{ ...reportStyles.summaryGrid, gridTemplateColumns: '1fr' }}>
          <SummaryCard label="Total Accounts Payable" value={data.totalPayable} color="warning" icon={DollarSign} formatCurrency={formatCurrency} />
        </div>

        <div style={reportStyles.sectionTitle}>
          <div style={reportStyles.sectionIcon}><FileText size={18} /></div>
          Outstanding Bills
        </div>

        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Supplier</th>
              <th style={reportStyles.tableHeader}>Bill #</th>
              <th style={reportStyles.tableHeader}>Date</th>
              <th style={reportStyles.tableHeader}>Due Date</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Amount Due</th>
              <th style={reportStyles.tableHeader}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.bills && data.bills.length > 0 ? (
              data.bills.map((bill, idx) => (
                <tr key={idx}>
                  <td style={reportStyles.tableCell}>{bill.supplier_name || bill.category || 'N/A'}</td>
                  <td style={reportStyles.tableCell}>{bill.bill_number || '-'}</td>
                  <td style={reportStyles.tableCell}>{bill.bill_date ? new Date(bill.bill_date).toLocaleDateString() : '-'}</td>
                  <td style={reportStyles.tableCell}>{bill.due_date ? new Date(bill.due_date).toLocaleDateString() : '-'}</td>
                  <td style={reportStyles.tableCellRight}>{formatCurrency(bill.amount_due || 0)}</td>
                  <td style={reportStyles.tableCell}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: bill.status === 'overdue' ? '#fef2f2' : '#fef3c7',
                      color: bill.status === 'overdue' ? '#dc2626' : '#d97706'
                    }}>
                      {bill.status || 'pending'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" style={{ ...reportStyles.tableCell, textAlign: 'center', color: '#64748b' }}>No outstanding bills</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={reportStyles.footer}>
        <div>?? {new Date().getFullYear()} {company?.name || 'Company Name'}</div>
      </div>
    </div>
  );
};

// Trial Balance Report
const TrialBalanceReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data) return <div style={{ padding: '40px', textAlign: 'center' }}>No data available</div>;
  
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader title="Trial Balance" dateRange={dateRange} company={company} />
      
      <div style={reportStyles.section}>
        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Account Code</th>
              <th style={reportStyles.tableHeader}>Account Name</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Debit</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Credit</th>
            </tr>
          </thead>
          <tbody>
            {data.accounts && data.accounts.length > 0 ? (
              <>
                {data.accounts.map((account, idx) => (
                  <tr key={idx}>
                    <td style={reportStyles.tableCell}>{account.code}</td>
                    <td style={reportStyles.tableCell}>{account.name}</td>
                    <td style={reportStyles.tableCellRight}>
                      {parseFloat(account.debit || 0) > 0 ? formatCurrency(account.debit) : '-'}
                    </td>
                    <td style={reportStyles.tableCellRight}>
                      {parseFloat(account.credit || 0) > 0 ? formatCurrency(account.credit) : '-'}
                    </td>
                  </tr>
                ))}
                <tr style={reportStyles.tableTotal}>
                  <td colSpan="2" style={{ ...reportStyles.tableCell, fontWeight: '700' }}>Total</td>
                  <td style={reportStyles.tableCellRight}>{formatCurrency(data.totalDebit || 0)}</td>
                  <td style={reportStyles.tableCellRight}>{formatCurrency(data.totalCredit || 0)}</td>
                </tr>
              </>
            ) : (
              <tr><td colSpan="4" style={{ ...reportStyles.tableCell, textAlign: 'center', color: '#64748b' }}>No account data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={reportStyles.footer}>
        <div>?? {new Date().getFullYear()} {company?.name || 'Company Name'}</div>
      </div>
    </div>
  );
};

// Fixed Asset Report
const FixedAssetReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data || !data.assets) return <div style={{ padding: '40px', textAlign: 'center' }}>No asset data available</div>;
  
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader title="Fixed Asset Register" dateRange={dateRange} company={company} asOfDate={data.asOfDate} />
      
      <div style={reportStyles.section}>
        {data.totals && (
          <div style={reportStyles.summaryGrid}>
            <SummaryCard label="Total Purchase Cost" value={data.totals.total_cost} color="info" icon={DollarSign} formatCurrency={formatCurrency} />
            <SummaryCard label="Accumulated Depreciation" value={data.totals.total_depreciation} color="warning" icon={TrendingDown} formatCurrency={formatCurrency} />
            <SummaryCard label="Net Book Value" value={data.totals.total_net_book_value} color="success" icon={TrendingUp} formatCurrency={formatCurrency} />
          </div>
        )}

        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Asset</th>
              <th style={reportStyles.tableHeader}>Category</th>
              <th style={reportStyles.tableHeader}>Purchase Date</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Cost</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Depreciation</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Net Book Value</th>
            </tr>
          </thead>
          <tbody>
            {data.assets.map((asset, idx) => (
              <tr key={idx}>
                <td style={reportStyles.tableCell}>{asset.asset_name}</td>
                <td style={reportStyles.tableCell}>{asset.asset_category || '-'}</td>
                <td style={reportStyles.tableCell}>{asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString() : '-'}</td>
                <td style={reportStyles.tableCellRight}>{formatCurrency(asset.purchase_cost || 0)}</td>
                <td style={reportStyles.tableCellRight}>{formatCurrency(asset.accumulated_depreciation || 0)}</td>
                <td style={reportStyles.tableCellRight}>{formatCurrency(asset.net_book_value || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={reportStyles.footer}>
        <div>?? {new Date().getFullYear()} {company?.name || 'Company Name'}</div>
      </div>
    </div>
  );
};

// Budget vs Actual Report
const BudgetVsActualReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data || !data.comparison) return <div style={{ padding: '40px', textAlign: 'center' }}>No budget data available</div>;
  
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader title="Budget vs. Actual Report" dateRange={dateRange} company={company} />
      
      <div style={reportStyles.section}>
        <table style={reportStyles.table}>
          <thead>
            <tr>
              <th style={reportStyles.tableHeader}>Category</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Budgeted</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Actual</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Variance</th>
              <th style={{ ...reportStyles.tableHeader, textAlign: 'right' }}>Variance %</th>
            </tr>
          </thead>
          <tbody>
            {data.comparison.map((item, idx) => (
              <tr key={idx}>
                <td style={reportStyles.tableCell}>{item.category}</td>
                <td style={reportStyles.tableCellRight}>{formatCurrency(item.budgeted || 0)}</td>
                <td style={reportStyles.tableCellRight}>{formatCurrency(item.actual || 0)}</td>
                <td style={{ ...reportStyles.tableCellRight, color: item.variance >= 0 ? '#10b981' : '#ef4444' }}>
                  {formatCurrency(item.variance || 0)}
                </td>
                <td style={{ ...reportStyles.tableCellRight, color: item.variance >= 0 ? '#10b981' : '#ef4444' }}>
                  {parseFloat(item.variancePercent || 0).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={reportStyles.footer}>
        <div>?? {new Date().getFullYear()} {company?.name || 'Company Name'}</div>
      </div>
    </div>
  );
};

// Department Performance Report
const DepartmentPerformanceReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data || !data.performance) return <div style={{ padding: '40px', textAlign: 'center' }}>No department data available</div>;
  
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader title="Department Performance" subtitle="Financial Performance by Segment" dateRange={dateRange} company={company} />
      
      <div style={reportStyles.section}>
        {data.performance.map((dept, idx) => (
          <div key={idx} style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: idx < data.performance.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
            <div style={{ ...reportStyles.sectionTitle, marginTop: 0 }}>
              <div style={reportStyles.sectionIcon}><Building2 size={18} /></div>
              {dept.departmentName}
            </div>
            <div style={reportStyles.summaryGrid}>
              <SummaryCard label="Income" value={dept.totalIncome} color="success" formatCurrency={formatCurrency} />
              <SummaryCard label="Expenses" value={dept.totalExpenses} color="danger" formatCurrency={formatCurrency} />
              <SummaryCard label="Net Income" value={dept.netIncome} color={dept.netIncome >= 0 ? 'success' : 'danger'} formatCurrency={formatCurrency} />
            </div>
          </div>
        ))}
      </div>

      <div style={reportStyles.footer}>
        <div>?? {new Date().getFullYear()} {company?.name || 'Company Name'}</div>
      </div>
    </div>
  );
};

// Notes to Financials Report
const NotesToFinancialsReport = ({ data, dateRange, company, formatCurrency }) => {
  if (!data) return <div style={{ padding: '40px', textAlign: 'center' }}>No notes data available</div>;
  
  const NoteSection = ({ number, title, children }) => (
    <div style={{ marginBottom: '28px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ backgroundColor: '#3b82f6', color: '#fff', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>{number}</span>
        {title}
      </h3>
      <div style={{ marginLeft: '32px', color: '#475569', lineHeight: 1.8 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      <ProfessionalHeader title="Notes to Financial Statements" dateRange={dateRange} company={company} />
      
      <div style={reportStyles.section}>
        <NoteSection number="1" title="Company Information">
          <p><strong>Company Name:</strong> {data.companyInformation?.name || 'N/A'}</p>
          <p><strong>Email:</strong> {data.companyInformation?.email || 'N/A'}</p>
          <p><strong>Address:</strong> {data.companyInformation?.address || 'N/A'}</p>
        </NoteSection>

        <NoteSection number="2" title="Accounting Policies">
          <p><strong>Reporting Period:</strong> {dateRange.startDate} to {dateRange.endDate}</p>
          <p><strong>Currency:</strong> {data.accountingPolicies?.currency || 'USD'}</p>
          <p><strong>Revenue Recognition:</strong> {data.accountingPolicies?.revenueRecognition || 'Accrual basis'}</p>
        </NoteSection>

        <NoteSection number="3" title="Revenue">
          <p><strong>Total Revenue:</strong> {formatCurrency(data.revenue?.totalRevenue || 0)}</p>
          <p><strong>Invoices Issued:</strong> {data.revenue?.invoicesIssued || 0}</p>
        </NoteSection>

        <NoteSection number="4" title="Expenses">
          <p><strong>Total Expenses:</strong> {formatCurrency(data.expenses?.totalExpenses || 0)}</p>
        </NoteSection>

        <NoteSection number="5" title="Receivables & Payables">
          <p><strong>Accounts Receivable:</strong> {formatCurrency(data.receivables?.totalReceivable || 0)}</p>
          <p><strong>Accounts Payable:</strong> {formatCurrency(data.payables?.totalPayable || 0)}</p>
        </NoteSection>
      </div>

      <div style={reportStyles.footer}>
        <div>?? {new Date().getFullYear()} {company?.name || 'Company Name'}</div>
      </div>
    </div>
  );
};

// Generic Report Display
const GenericReportDisplay = ({ data, dateRange, company, formatCurrency }) => (
  <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
    <ProfessionalHeader title={data.reportName || "Report"} dateRange={dateRange} company={company} />
    <div style={reportStyles.section}>
      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '13px', backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px' }}>
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  </div>
);

export default Reports;
